import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../Global/Layout';
import { showSuccess, showError, showConfirm } from '../../../utils/sweetAlertConfig';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';
import { API_CONFIG, LOTTO_TYPE, LOTTO_BALL_TYPE, LOTTO_BALL_TYPE_NUMBER, LOTTO_TYPE_INPUT, LOTTO_TYPE_DISPLAY, extractTimeFromTimestamp, dateStampToDateDB, toLocalDatetimeStr, parseDatetimeLocal } from '../../../contexts/constants';

const SummaryPrizes = ({ onLogout, userRole, lotType }) => {
	const lottoType = lotType;
	const navigate = useNavigate();

	const [youtubeUrl, setYoutubeUrl] = useState('');
	const [winningNumbers, setWinningNumbers] = useState(['', '', '', '', '']);
	const [powerBall, setPowerBall] = useState('');
	const [showNumberModal, setShowNumberModal] = useState(false);
	const [fullAwardData, setFullAwardData] = useState(null);
	const [loadingData, setLoadingData] = useState(true);

	useBodyScrollLock(showNumberModal);

	useEffect(() => {
		const loadLatestAward = async () => {
			setLoadingData(true);
			try {
				const token = localStorage.getItem('token');
				// ดึงเฉพาะรายการที่ยังไม่สรุปผล และวันออกรางวัลน้อยกว่าวันปัจจุบัน
				const today = dateStampToDateDB(new Date());
				const params = new URLSearchParams({
					type: lottoType,
					limit: 1,
					summaryStatus: 'false',
					endDate: today
				});

				const response = await fetch(`${API_CONFIG.BASE_URL}/awards?${params.toString()}`, {
					headers: { 'Authorization': `Bearer ${token}` }
				});

				if (!response.ok) throw new Error('Failed to fetch award data');

				const result = await response.json();
				const latest = result.data?.[0] ?? result[0] ?? null;

				if (!latest) {
					setLoadingData(false);
					return;
				}

				// ดึงรายละเอียดเต็มด้วย id
				const detailRes = await fetch(`${API_CONFIG.BASE_URL}/awards/${latest.id}`, {
					headers: { 'Authorization': `Bearer ${token}` }
				});

				if (!detailRes.ok) throw new Error('Failed to fetch award detail');

				const awardData = await detailRes.json();
				setFullAwardData(awardData);
				setYoutubeUrl(awardData.youtubeLink || '');
				setWinningNumbers((awardData.winningNumbers || []).length > 0 ? awardData.winningNumbers?.map(n => String(n)) : ['', '', '', '', '']);
				setPowerBall(awardData.powerBall ? String(awardData.powerBall) : '');
			} catch (error) {
				console.error('Error loading award data:', error);
				showError('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลผลรางวัลได้');
			} finally {
				setLoadingData(false);
			}
		};

		loadLatestAward();
	}, [lottoType]);

	if (!['sysadmin', 'owner'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

	const maxWhiteBall = LOTTO_BALL_TYPE_NUMBER[lottoType]?.[0]?.['whiteball'] ?? 69;
	const maxSuperBall = LOTTO_BALL_TYPE_NUMBER[lottoType]?.[0]?.['superball'] ?? 26;

	const handleNumberChange = (index, value) => {
		if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 1 && parseInt(value) <= maxWhiteBall)) {
			const newNumbers = [...winningNumbers];
			newNumbers[index] = value;
			setWinningNumbers(newNumbers);
		}
	};

	const handleNumberSelect = (index, value) => {
		const newNumbers = [...winningNumbers];
		newNumbers[index] = value;
		setWinningNumbers(newNumbers);
	};

	const handlePowerBallChange = (value) => {
		if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 1 && parseInt(value) <= maxSuperBall)) {
			setPowerBall(value);
		}
	};

	const handlePowerBallSelect = (value) => {
		setPowerBall(prev => prev === value ? '' : value);
	};

	const handleResetNumbers = () => {
		setWinningNumbers(['', '', '', '', '']);
		setPowerBall('');
	};

	const handleSubmit = async () => {
		if (!fullAwardData) {
			showError('เกิดข้อผิดพลาด', 'ไม่พบข้อมูลผลรางวัล');
			return;
		}

		const result = await showConfirm({
			title: 'ยืนยันการสรุปผลรางวัล',
			html: 'กรุณาตรวจสอบความถูกต้องให้ครบถ้วนก่อนสรุปผลรางวัล <span style="color:red">เมื่อสรุปผลรางวัลแล้ว จะไม่สามารถแก้ไขได้อีก</span><br/>คุณต้องการสรุปผลรางวัลใช่หรือไม่?',
			confirmText: 'ยืนยัน',
			cancelText: 'ยกเลิก',
			confirmVariant: 'warning',
			reverseButtons: true
		});

		if (!result.isConfirmed) return;

		const parseNumber = (value) => {
			if (!value) return null;
			return parseFloat(String(value).replace(/,/g, ''));
		};

		const awardData = fullAwardData;

		const submitData = {
			lotType: lottoType,
			prizeAnnouncementDate: toLocalDatetimeStr(awardData.drawDate),
			orderAcceptanceDate: toLocalDatetimeStr(awardData.orderAcceptanceDate),
			orderClosingDate: toLocalDatetimeStr(awardData.orderClosingDate),
			orderActive: awardData.status || false,
			summaryStatus: true,
			youtubeLink: youtubeUrl,
			salePrice: parseNumber(awardData.salePrices?.default),
			salePriceNormal: parseNumber(awardData.salePrices?.normal),
			salePriceSilver: parseNumber(awardData.salePrices?.silver),
			salePriceGold: parseNumber(awardData.salePrices?.gold),
			winBall: winningNumbers,
			winBallSpecial: powerBall,
			prizes: awardData.prizes ? {
				match_5_1: { bath: awardData.prizes.match_5_1?.bath ?? null, dollar: awardData.prizes.match_5_1?.dollar ?? null },
				match_5_0: { bath: awardData.prizes.match_5_0?.bath ?? null, dollar: awardData.prizes.match_5_0?.dollar ?? null },
				match_4_1: { bath: awardData.prizes.match_4_1?.bath ?? null, dollar: awardData.prizes.match_4_1?.dollar ?? null },
				match_4_0: { bath: awardData.prizes.match_4_0?.bath ?? null, dollar: awardData.prizes.match_4_0?.dollar ?? null },
				match_3_1: { bath: awardData.prizes.match_3_1?.bath ?? null, dollar: awardData.prizes.match_3_1?.dollar ?? null },
				match_3_0: { bath: awardData.prizes.match_3_0?.bath ?? null, dollar: awardData.prizes.match_3_0?.dollar ?? null },
				match_2_1: { bath: awardData.prizes.match_2_1?.bath ?? null, dollar: awardData.prizes.match_2_1?.dollar ?? null },
				match_1_1: { bath: awardData.prizes.match_1_1?.bath ?? null, dollar: awardData.prizes.match_1_1?.dollar ?? null },
				match_0_1: { bath: awardData.prizes.match_0_1?.bath ?? null, dollar: awardData.prizes.match_0_1?.dollar ?? null }
			} : {}
		};

		try {
			const token = localStorage.getItem('token');
			const response = await fetch(`${API_CONFIG.BASE_URL}/awards/${fullAwardData.id}`, {
				method: 'PUT',
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(submitData)
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.message || 'Failed to save award result');
			}

			showSuccess('บันทึกสำเร็จ', 'สรุปผลรางวัลเรียบร้อยแล้ว');
			const membersParams = new URLSearchParams({
				periodId: fullAwardData.id,
				lotType: lottoType,
				periodLabel: formatDateTH(fullAwardData.drawDate),
			});
			navigate(`/report/members-winning?${membersParams.toString()}`);
		} catch (error) {
			console.error('Error saving award result:', error);
			showError('เกิดข้อผิดพลาด', error.message || 'ไม่สามารถบันทึกผลรางวัลได้');
		}
	};

	const whiteBallInputClass = lottoType === 'powerball'
		? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
		: 'border-blue-300 dark:border-blue-600 bg-white dark:bg-blue-800 text-blue-900 dark:text-white';

	const whiteBallSelectedClass = lottoType === 'powerball'
		? 'bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-300 dark:to-gray-400 border-gray-400 text-gray-900'
		: 'bg-gradient-to-br from-blue-200 to-blue-300 dark:from-blue-300 dark:to-blue-400 border-blue-400 text-blue-900';

	const whiteBallUnselectedClass = lottoType === 'powerball'
		? 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-600'
		: 'bg-blue-100 dark:bg-blue-800 border-blue-300 dark:border-blue-600 text-blue-400 dark:text-blue-600';

	const whiteBallGridClass = lottoType === 'powerball'
		? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-blue-100 dark:hover:bg-gray-600 hover:scale-110'
		: 'bg-blue-100 dark:bg-blue-700 text-blue-900 dark:text-white hover:bg-blue-100 dark:hover:bg-blue-600 hover:scale-110';

	const superBallLabel = lottoType === 'powerball'
		? `เลือกบอลแดง (Powerball 1-${maxSuperBall})`
		: `เลือกบอลเหลือง (Mega Ball 1-${maxSuperBall})`;

	const formatDateTH = (datetime) => {
		if (!datetime) return '-';
		return parseDatetimeLocal(datetime).toLocaleDateString('th-TH', {
			year: 'numeric', month: 'long', day: 'numeric'
		});
	};

	return (
		<Layout title={'สรุปผลรางวัล ' + LOTTO_TYPE[lottoType]} onLogout={onLogout} userRole={userRole}>
			<div className="max-w-6xl mx-auto">
				{/* Header note */}
				<div className="mb-4">
					<p className="text-sm text-gray-500 dark:text-gray-400">
						แสดงรายการล่าสุดที่ยังไม่ได้สรุปผล โดยวันที่ออกรางวัลต้องน้อยกว่าวันปัจจุบัน
					</p>
				</div>

				{loadingData ? (
					<div className="bg-white dark:bg-surface-dark border border-borderc-light dark:border-borderc-dark rounded-xl shadow-card dark:shadow-carddark p-10 flex justify-center items-center">
						<span className="text-gray-500 dark:text-gray-400">กำลังโหลดข้อมูล...</span>
					</div>
				) : !fullAwardData ? (
					<div className="bg-white dark:bg-surface-dark border border-borderc-light dark:border-borderc-dark rounded-xl shadow-card dark:shadow-carddark p-6">
						<p className="text-gray-600 dark:text-gray-400">ไม่พบรายการ {LOTTO_TYPE[lottoType]} ที่รอสรุปผล (วันออกรางวัลน้อยกว่าวันปัจจุบัน)</p>
						<div className="mt-4">
							<button
								type="button"
								onClick={() => navigate(`/manage_data/prizes-summary-history?type=${lottoType}`)}
								className="px-6 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition"
							>
								กลับ
							</button>
						</div>
					</div>
				) : (
					<div className="space-y-6">

						{/* Draw Info Card */}
						<div className="bg-white dark:bg-surface-dark border border-borderc-light dark:border-borderc-dark rounded-xl shadow-card dark:shadow-carddark p-6">
							<h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">รายละเอียด</h2>
							<div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
								<div>
									<p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">ประเภท</p>
									<span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
										lottoType === 'powerball'
											? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
											: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
									}`}>
										{LOTTO_TYPE[lottoType]}
									</span>
								</div>
								<div>
									<p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">งวดที่ออกรางวัล</p>
									<p className="text-sm font-semibold text-gray-900 dark:text-white">
										{formatDateTH(fullAwardData.drawDate)}
									</p>
									<p className="text-xs text-gray-500 dark:text-gray-400">
										{extractTimeFromTimestamp(fullAwardData.drawDate)} น.
									</p>
								</div>
								<div>
									<p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">วันที่เปิดรับคำสั่งซื้อ</p>
									<p className="text-sm font-semibold text-gray-900 dark:text-white">
										{fullAwardData.orderAcceptanceDate ? formatDateTH(fullAwardData.orderAcceptanceDate) : '-'}
									</p>
									<p className="text-xs text-gray-500 dark:text-gray-400">
										{fullAwardData.orderAcceptanceDate ? extractTimeFromTimestamp(fullAwardData.orderAcceptanceDate) + ' น.' : ''}
									</p>
								</div>
								<div>
									<p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">วันที่ปิดรับคำสั่งซื้อ</p>
									<p className="text-sm font-semibold text-gray-900 dark:text-white">
										{fullAwardData.orderClosingDate ? formatDateTH(fullAwardData.orderClosingDate) : '-'}
									</p>
									<p className="text-xs text-gray-500 dark:text-gray-400">
										{fullAwardData.orderClosingDate ? extractTimeFromTimestamp(fullAwardData.orderClosingDate) + ' น.' : ''}
									</p>
								</div>
							</div>
						</div>

						{/* YouTube URL */}
						<div className="bg-white dark:bg-surface-dark border border-borderc-light dark:border-borderc-dark rounded-xl shadow-card dark:shadow-carddark p-6">
							<h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">ลิงก์ YouTube</h2>
							<label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
								ลิงก์ YouTube
							</label>
							<input
								type="url"
								value={youtubeUrl}
								onChange={(e) => setYoutubeUrl(e.target.value)}
								placeholder="https://www.youtube.com/watch?v=..."
								className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>
						</div>

						{/* Winning Numbers */}
						<div className="bg-white dark:bg-surface-dark border border-borderc-light dark:border-borderc-dark rounded-xl shadow-card dark:shadow-carddark p-6">
							<h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">เลขผลรางวัล</h2>
							<div className="flex flex-wrap items-center gap-4">
								<div className="flex gap-2">
									{(winningNumbers).map((num, index) => (
										<div key={index} className="flex flex-col items-center">
											<label className="text-xs text-gray-600 dark:text-gray-400 mb-1">บอล {index + 1}</label>
											<input
												type="text"
												value={num}
												onChange={(e) => handleNumberChange(index, e.target.value)}
												maxLength={2}
												className={`w-12 h-12 text-center border-2 rounded-full font-bold focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${whiteBallInputClass}`}
											/>
										</div>
									))}
								</div>
								<span className="text-2xl font-bold text-gray-600 dark:text-gray-400">+</span>
								<div className="flex flex-col items-center">
									<label className="text-xs text-gray-600 dark:text-gray-400 mb-1">{LOTTO_BALL_TYPE[lottoType]}</label>
									<input
										type="text"
										value={powerBall}
										onChange={(e) => handlePowerBallChange(e.target.value)}
										maxLength={2}
										className={`w-12 h-12 text-center border-2 rounded-full font-bold focus:ring-2 ${LOTTO_TYPE_INPUT[lottoType]}`}
									/>
								</div>
							</div>

							<div className="mt-3 flex items-center gap-2">
								<button
									type="button"
									onClick={() => setShowNumberModal(true)}
									className="text-sm px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
								>
									เลือกเลข
								</button>
								<button
									type="button"
									onClick={handleResetNumbers}
									className="text-sm px-4 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors"
								>
									ล้าง
								</button>
							</div>
							<p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
								คุณสามารถพิมพ์ตัวเลขในช่อง หรือกดปุ่ม "เลือกเลข" เพื่อเลือกจากตัวเลขได้
							</p>
						</div>

						{/* Buttons */}
						<div className="flex justify-end gap-4">
							<button
								type="button"
								onClick={() => navigate(`/manage_data/prizes-summary-history?type=${lottoType}`)}
								className="px-6 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition"
							>
								ยกเลิก
							</button>
							<button
								type="button"
								onClick={handleSubmit}
								className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition"
							>
								สรุปผลรางวัล
							</button>
						</div>
					</div>
				)}
			</div>

			{/* Number Selection Modal */}
			{showNumberModal && (
				<div className="fixed top-0 inset-x-0 bg-black/50 flex items-top justify-center z-50 p-4 max-h-screen h-full overflow-y-auto flex align-middle">
					<div className="flex align-middle h-auto my-auto">
						<div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl h-auto w-full max-w-lg md:min-w-[600px] my-5">
							<div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
								<h3 className="text-xl font-bold text-gray-900 dark:text-white">เลือกเลขที่ออก</h3>
								<button
									onClick={() => setShowNumberModal(false)}
									className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
									aria-label="ปิด"
								>
									<svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>

							<div className="p-6 space-y-6">
								{/* Selected Numbers Display */}
								<div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
									<h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">เลขที่เลือก</h4>
									<div className="flex flex-wrap items-center gap-3">
										<div className="flex gap-2">
											{winningNumbers.map((num, index) => (
												<div
													key={index}
													className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg border-2 ${num ? whiteBallSelectedClass : whiteBallUnselectedClass}`}
												>
													{num || '-'}
												</div>
											))}
										</div>
										<span className="text-2xl font-bold text-gray-600 dark:text-gray-400">+</span>
										<div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg border-2 ${powerBall ? LOTTO_TYPE_DISPLAY[lottoType] : LOTTO_TYPE_INPUT[lottoType]}`}>
											{powerBall || '-'}
										</div>
									</div>
								</div>

								{/* White Ball Selection */}
								<div>
									<h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
										{lottoType === 'powerball' ? `เลือกบอลขาว (1-${maxWhiteBall})` : `เลือกบอลฟ้า (1-${maxWhiteBall})`}
									</h4>
									<div className="grid grid-cols-10 gap-2">
										{Array.from({ length: maxWhiteBall }, (_, i) => i + 1).map((n) => (
											<button
												key={n}
												type="button"
												onClick={() => {
													const numStr = String(n);
													const existingIndex = winningNumbers.findIndex(num => num === numStr);
													if (existingIndex !== -1) {
														const newNumbers = [...winningNumbers];
														newNumbers[existingIndex] = '';
														setWinningNumbers(newNumbers);
													} else {
														const emptyIndex = winningNumbers.findIndex(num => num === '');
														if (emptyIndex !== -1) handleNumberSelect(emptyIndex, numStr);
													}
												}}
												className={`w-10 h-10 rounded-full text-sm font-semibold transition-all ${winningNumbers.includes(String(n)) ? 'bg-blue-500 text-white shadow-md' : whiteBallGridClass}`}
											>
												{n}
											</button>
										))}
									</div>
								</div>

								{/* Special Ball Selection */}
								<div>
									<h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{superBallLabel}</h4>
									<div className="grid grid-cols-10 gap-2">
										{Array.from({ length: maxSuperBall }, (_, i) => i + 1).map((n) => (
											<button
												key={n}
												type="button"
												onClick={() => handlePowerBallSelect(String(n))}
												className={`w-10 h-10 rounded-full text-sm font-semibold transition-all ${powerBall === String(n) ? LOTTO_TYPE_DISPLAY[lottoType] : LOTTO_TYPE_INPUT[lottoType] + ' hover:scale-110'}`}
											>
												{n}
											</button>
										))}
									</div>
								</div>

								<div className="flex justify-end gap-3 pt-4">
									<button
										type="button"
										onClick={() => setShowNumberModal(false)}
										className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-lg transition-colors"
									>
										เสร็จสิ้น
									</button>
									<button
										type="button"
										onClick={handleResetNumbers}
										className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors"
									>
										ล้าง
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</Layout>
	);
};

export default SummaryPrizes;
